import { useProductOverviewStore } from "./stores/ProductOverviewStore";
import { defineStore } from "pinia";
import { FilterFacet, RangeValues } from "@daalder/types";

let productOverviewStore: Omit<ReturnType<typeof useProductOverviewStore>, keyof ReturnType<typeof defineStore>> = null;

/**
 * This helper is used to work with the data returned from Pionect\App\Elements\ProductOverviewFilters.
 * It is not possible to typehint this data, since it has unique keys to add the ability to conditionally display fields
 * in the elementor editor.
 */
export function useProductFilterWidgetHelper() {
    function toggleProductFilter(filtersData: any[], replaceExistingFilters: boolean = false) {
        if (productOverviewStore === null) {
            productOverviewStore = useProductOverviewStore();
        }

        let isAnyFilterAlreadyEnabled: boolean = false;

        filtersData.forEach((filterData) => {
            if (filterIsActive(filterData)) {
                isAnyFilterAlreadyEnabled = true;
            }
        });

        // This must be done after it is determined if any filter in filtersData is already active, otherwise the
        // toggling functionality (on/off) doesn't work.
        if (replaceExistingFilters) {
            productOverviewStore.clearAllFilters();
        }

        filtersData.forEach((filterData) => {
            let filterKey = filterData?.filter_code;

            // If the filter is a range filter (determine using key names).
            if (
                filterData &&
                (filterData[`${filterKey}_value_range_min`] || filterData[`${filterKey}_value_range_max`])
            ) {
                // Clear the filter if already enabled
                if (isAnyFilterAlreadyEnabled) {
                    productOverviewStore.clearFilter(filterKey);
                } else {
                    // If there are no already enabled filters in this filtersData set, enable the filter.
                    let rangeValues: RangeValues = {
                        min: filterData[`${filterKey}_value_range_min`] ?? 0,
                        max: filterData[`${filterKey}_value_range_max`] ?? Number.MAX_SAFE_INTEGER,
                    };

                    // Update the filter using the range values from the Widget settings.
                    productOverviewStore.setRangeFilter(filterKey, rangeValues);
                }
            } else if (filterData[`${filterKey}_value_select`]) {
                // Else, if the filter is a select filter (determine using key names).
                // Update the filter using the (multi-)values from the Widget settings.
                // The last argument of this method determines whether the checkboxes will be checked or unchecked.
                productOverviewStore.setCheckboxFilter(
                    filterKey,
                    filterData[`${filterKey}_value_select`],
                    !isAnyFilterAlreadyEnabled,
                );
            }

            // todo: boolean filter
        });
    }

    function allFiltersAreActive(filters: any[]) {
        let allActive: boolean = true;

        filters.forEach((filter) => {
            if (!filterIsActive(filter)) {
                allActive = false;
            }
        });

        return allActive;
    }

    function filterIsActive(filterData: any) {
        if (productOverviewStore === null) {
            productOverviewStore = useProductOverviewStore();
        }

        let filterKey = filterData?.filter_code;
        let filter = productOverviewStore.getFilter(filterKey);

        if (!filter) {
            return false;
        }

        // If the filterData is for a range filter
        if (filterData && (filterData[`${filterKey}_value_range_min`] || filterData[`${filterKey}_value_range_max`])) {
            let min = filterData[`${filterKey}_value_range_min`] ?? 0;
            let max = filterData[`${filterKey}_value_range_max`] ?? Number.MAX_SAFE_INTEGER;

            if (filter.configuredRange.min == min && filter.configuredRange.max == max) {
                return true;
            }
        } else if (filterData[`${filterKey}_value_select`]) {
            // Else, if the filterData is for a select/checkbox filter
            let checkBoxesNames = filterData[`${filterKey}_value_select`];
            let allCheckboxesAreChecked: boolean = true;

            checkBoxesNames.forEach((checkboxName) => {
                let checkBoxIndex = (filter.facets as FilterFacet[]).findIndex((facet) => {
                    return facet.name === checkboxName;
                });

                if (checkBoxIndex == -1 || !filter.selectedCheckboxes[checkBoxIndex]) {
                    allCheckboxesAreChecked = false;
                }
            });

            return allCheckboxesAreChecked;
        }

        return false;
    }

    function doesFilterOptionResultInNoProducts(filterData: any) {
        if (productOverviewStore === null) {
            productOverviewStore = useProductOverviewStore();
        }

        let filterKey = filterData?.filter_code;
        let filter = productOverviewStore.getFilter(filterKey);

        // Adding a filter that is not returned from the API on this Category will probably result in 0 products.
        if (!filter) {
            return true;
        }

        // If the filterData is for a range filter
        if (filterData && (filterData[`${filterKey}_value_range_min`] || filterData[`${filterKey}_value_range_max`])) {
            let rangeValues: RangeValues = {
                min: filterData[`${filterKey}_value_range_min`] ?? 0,
                max: filterData[`${filterKey}_value_range_max`] ?? Number.MAX_SAFE_INTEGER,
            };

            return !productOverviewStore.getHasProductsIfFilterWouldBeAdded(filterKey, rangeValues, null);
        } else if (filterData[`${filterKey}_value_select`]) {
            return !productOverviewStore.getHasProductsIfFilterWouldBeAdded(
                filterKey,
                null,
                filterData[`${filterKey}_value_select`],
            );
        }

        // This filter is not properly formatted / not supported
        return true;
    }

    return {
        toggleProductFilter,
        allFiltersAreActive,
        filterIsActive,
        doesFilterOptionResultInNoProducts,
    };
}
